import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { apiClient } from '@/services/core/apiClient';
import { useToast } from '@/hooks/use-toast';

export interface ClinicUserGroupUser {
  userId: number;
  userName: string;
  appUserName: string | null;
}

export interface ClinicUserGroupClinic {
  clinicId: number;
  clinicName: string;
}

export interface ClinicUserGroupListItem {
  clinicUserGroupId: number;
  groupName: string;
  totalUserCount: number;
  createdAt: string;
  users: ClinicUserGroupUser[];
  clinics: ClinicUserGroupClinic[];
}

interface ClinicUserGroupsPagination {
  page: number;
  size: number;
  totalRecords: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

interface ClinicUserGroupsApiResponse {
  pagination: ClinicUserGroupsPagination;
  data: ClinicUserGroupListItem[];
  success: boolean;
  timestamp: number;
}

export interface UseClinicUserGroupsResult {
  clinicUserGroups: ClinicUserGroupListItem[];
  loading: boolean;
  currentPage: number;
  totalPages: number;
  totalCount: number;
  pageSize: number;
  searchValue: string;
  hasNext: boolean;
  hasPrevious: boolean;
  setCurrentPage: (page: number) => void;
  setPageSize: (size: number) => void;
  setSearchValue: (value: string) => void;
  refetch: () => void;
}

export const useClinicUserGroups = (): UseClinicUserGroupsResult => {
  const { user, token } = useAuth();
  const { toast } = useToast();

  const [clinicUserGroups, setClinicUserGroups] = useState<ClinicUserGroupListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchValue, setSearchValue] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrevious, setHasPrevious] = useState(false);

  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchClinicUserGroups = useCallback(async (page: number, size: number, search: string) => {
    if (!user?.id || !token) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        size: size.toString(),
      });
      if (search.trim()) {
        params.append('search', search.trim());
      }

      const endpoint = `/api/ngpep/${user.id}/clinic-user-groups/all/details?${params.toString()}`;
      const response = await apiClient.get<ClinicUserGroupsApiResponse>(endpoint, token);

      if (response.success && response.data?.success) {
        const apiData = response.data;
        setClinicUserGroups(apiData.data || []);
        setTotalCount(apiData.pagination.totalRecords || 0);
        setTotalPages(apiData.pagination.totalPages || 0);
        setHasNext(apiData.pagination.hasNext || false);
        setHasPrevious(apiData.pagination.hasPrevious || false);
      } else {
        toast({
          title: 'Error',
          description: response.error || 'Failed to fetch clinic user groups',
          variant: 'destructive',
        });
        setClinicUserGroups([]);
        setTotalCount(0);
        setTotalPages(0);
      }
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message || 'Failed to fetch clinic user groups',
        variant: 'destructive',
      });
      setClinicUserGroups([]);
      setTotalCount(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [user?.id, token, toast]);

  useEffect(() => {
    fetchClinicUserGroups(currentPage, pageSize, searchValue);
  }, [currentPage, pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced search
  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    debounceTimer.current = setTimeout(() => {
      setCurrentPage(0);
      fetchClinicUserGroups(0, pageSize, searchValue);
    }, 500);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [searchValue]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSetPageSize = useCallback((size: number) => {
    setPageSize(size);
    setCurrentPage(0);
  }, []);

  const refetch = useCallback(() => {
    fetchClinicUserGroups(currentPage, pageSize, searchValue);
  }, [fetchClinicUserGroups, currentPage, pageSize, searchValue]);

  return {
    clinicUserGroups,
    loading,
    currentPage,
    totalPages,
    totalCount,
    pageSize,
    searchValue,
    hasNext,
    hasPrevious,
    setCurrentPage,
    setPageSize: handleSetPageSize,
    setSearchValue,
    refetch,
  };
};
